import { FileText, X, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { cn } from "@/lib/cn";

import { formatBytes } from "./constants";

/**
 * 업로드 검증 실패 사유.
 *
 * i18n 도입 후엔 사용자 친화 메시지를 stored 문자열로 두면 언어 변경
 * 시 stale 텍스트가 발생한다. 이를 피하기 위해 ``code`` (와 필요한
 * 파라미터) 만 저장하고, 렌더 시점에 ``t()`` 로 해석한다.
 */
export type ValidationError =
  | { code: "not_pdf" }
  | { code: "empty" }
  | { code: "too_large"; maxMb: number };

export interface FileEntry {
  /** UI 식별자 — File 객체 자체는 동등성 비교가 불안정해 별도 키 사용. */
  key: string;
  file: File;
  /** 검증 실패 사유. null 이면 제출 가능. */
  error: ValidationError | null;
}

interface Props {
  entry: FileEntry;
  onRemove: (key: string) => void;
  disabled?: boolean;
}

export default function FileItem({ entry, onRemove, disabled }: Props) {
  const { t } = useTranslation();
  const { key, file, error } = entry;
  const invalid = !!error;

  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-md border px-3 py-2 text-sm",
        invalid ? "border-red-200 bg-red-50" : "border-slate-200 bg-white hover:border-slate-300",
      )}
    >
      {invalid ? (
        <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-500" />
      ) : (
        <FileText className="h-4 w-4 flex-shrink-0 text-slate-400" />
      )}

      <div className="min-w-0 flex-1">
        <p
          className={cn("truncate text-sm", invalid ? "text-red-700" : "text-slate-700")}
          title={file.name}
        >
          {file.name}
        </p>
        <p className={cn("mt-0.5 text-[11px]", invalid ? "text-red-600" : "text-slate-400")}>
          {error ? renderError(t, error) : formatBytes(file.size)}
        </p>
      </div>

      <button
        type="button"
        onClick={() => onRemove(key)}
        disabled={disabled}
        className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
        aria-label={t("upload.list.removeAriaLabel", { name: file.name })}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

/**
 * ``ValidationError`` → 현재 언어의 사용자 친화 메시지.
 *
 * caller 가 ``useTranslation`` 의 ``t`` 함수를 주입 — hook 외부에서도
 * 활용 가능하도록 의존성 inversion. ``code`` 만 가진 변형 (``not_pdf``,
 * ``empty``) 와 파라미터를 가진 변형 (``too_large.maxMb``) 둘 다 처리.
 */
function renderError(t: TFunction, err: ValidationError): string {
  switch (err.code) {
    case "not_pdf":
      return t("upload.validation.notPdf");
    case "empty":
      return t("upload.validation.empty");
    case "too_large":
      return t("upload.validation.tooLarge", { mb: err.maxMb });
  }
}
